import { useEffect, useMemo } from 'react';
import { useBusinessAddressQuery } from 'store/api/apiSlice';
import { useAppSelector } from 'store/hooks';
import { useApiError } from './useApiError';
import { shouldFetchReports } from './fetchEligibility';
import { usePollingOnError } from 'shared/hooks/usePollingOnError';
import {
  BusinessAddressRequest,
  BusinessAddressResponse,
} from 'store/api/apiTypes';

/**
 * useBusinessAddress
 * Fetches Business Address information completeness rule for selected project domain.
 */
export const useBusinessAddress = () => {
  const selectedProject = useAppSelector(
    state => state.project.selectedProject
  );
  const domain = selectedProject?.domain;

  const activeReport = useAppSelector(state => state.history.activeReport);
  const queryArgs = useMemo<BusinessAddressRequest | null>(() => {
    if (!domain) return null;
    if (!shouldFetchReports(selectedProject)) return null;
    return { domain, reportId: activeReport?.id };
  }, [domain, selectedProject, activeReport]);

  const { data, error, isLoading, isFetching, refetch } =
    useBusinessAddressQuery(queryArgs as BusinessAddressRequest, {
      skip: !queryArgs,
      refetchOnMountOrArgChange: true,
    });

  const { showErrorModal, handleApiError, handleRetry, hideErrorModal } =
    useApiError(() => refetch());

  useEffect(() => {
    if (error) handleApiError(error as any);
  }, [error, handleApiError]);

  usePollingOnError(queryArgs, error, refetch, data);

  const rule = data as BusinessAddressResponse | undefined;

  return {
    businessAddress: rule,
    pageUrls: rule?.page_urls_analyzed || [],
    identified: rule?.business_address_identified,
    extraction: rule?.business_address_extraction,
    scoring: rule?.scoring_breakdown || {},
    finalScore: rule?.final_score,
    finalCalculation: rule?.final_calculation,
    overallAssessment: rule?.overall_assessment,
    priorityImprovements: rule?.priority_improvements || [],
    isLoading,
    isFetching,
    error,
    refetchBusinessAddress: refetch,
    showBusinessAddressErrorModal: showErrorModal,
    retryBusinessAddress: handleRetry,
    hideBusinessAddressErrorModal: hideErrorModal,
    hasBusinessAddress: !!rule,
    queryArgs,
  };
};
